import React, { ChangeEvent } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';

import {
  CustomSelectChoice,
  FieldAnswer,
  FieldChoice,
  SelectChoiceField,
} from '@app/api';

import { CheckboxBlockField } from '@app/form';

import { Autocomplete } from './Autocomplete';
import { ChoicesLabel } from './MultipleChoice/ChoicesLabel';

const useStyles = createUseStyles((theme: Theme) => ({
  checkbox: {
    marginTop: theme.marginBase * 2,
    backgroundColor: theme.colors.white,
  },
}));

interface Props {
  field: SelectChoiceField;
}

export const SingleChoice = ({ field }: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });

  const { t } = useTranslation();

  const choices =
    field.properties && field.properties.choices
      ? field.properties.choices
      : [];

  const { values, setFieldValue } = useFormikContext<FieldAnswer>();

  const emptyChoices = () => {
    setFieldValue('choice', {
      ref: '',
      choice: '',
    });
    return true;
  };

  const onChangeValueOther = (choice: FieldChoice, value: string) => {
    setFieldValue('choice', {
      ref: choice.ref,
      label: value,
      value: choice.value || choice.ref,
    });
  };

  if (field.properties.autocomplete) {
    return <Autocomplete field={field} />;
  }

  return (
    <>
      <ChoicesLabel field={field} />
      {choices.map((choice, index) => {
        const otherField = choice.ref === CustomSelectChoice.Other;
        return (
          <CheckboxBlockField
            key={`choice-${field.id}-${index}`}
            className={classes.checkbox}
            label={
              choice.label ||
              (t('screeningForm.noLabel', { index: index + 1 }) as string)
            }
            editable={otherField}
            otherPlaceholder={t('screeningForm.otherPlaceholder')}
            type="radio"
            checked={values.choice && values.choice.ref === choice.ref}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              if (e.currentTarget.checked) {
                setFieldValue('choice', {
                  ref: choice.ref,
                  label: choice.label,
                  value: choice.value || choice.ref,
                  metadata: choice.metadata,
                });
              } else {
                emptyChoices();
              }
            }}
            onEditValueChange={
              otherField
                ? (value: string) => onChangeValueOther(choice, value)
                : undefined
            }
          />
        );
      })}
    </>
  );
};
